import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageTransition } from '../components/ui/PageTransition';
import { SEO } from '../components/shared/SEO';
import { AnimatedSection } from '../components/ui/AnimatedSection';
import { Button } from '../components/ui/Button';
import { API_ENDPOINTS } from '../config/api';
import { Footer } from '../components/layout/Footer';
import toast from 'react-hot-toast';

interface PaymentDetails {
  reference: string;
  merchant_ref: string;
  payment_method: string;
  payment_name: string;
  customer_name: string;
  amount: number;
  fee_merchant: number;
  fee_customer: number;
  total_fee: number;
  amount_received: number;
  pay_code: string;
  qr_url?: string;
  status: string;
  expired_time: number;
  instructions: {
    title: string;
    steps: string[];
  }[];
}

export function Checkout() {
  const { t } = useTranslation();
  const { reference } = useParams<{ reference: string }>();
  const navigate = useNavigate();
  const pollIntervalRef = useRef<NodeJS.Timeout>();
  const [payment, setPayment] = useState<PaymentDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState<string>('');
  const [lastStatus, setLastStatus] = useState<string | null>(null);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      if (!reference) {
        setError('Invalid payment reference');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(API_ENDPOINTS.store.paymentDetails(reference));
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(
            errorData?.error || 
            `Failed to fetch payment details (${response.status})`
          );
        }
        const data = await response.json();
        setPayment(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load payment details';
        setError(errorMessage);
        toast.error(t('store.checkout.errorFetchingDetails'));
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [reference, t]);

  useEffect(() => {
    if (!payment) return;

    const timer = setInterval(() => {
      const now = Math.floor(Date.now() / 1000);
      const timeRemaining = payment.expired_time - now;

      if (timeRemaining <= 0) {
        setTimeLeft(t('store.checkout.expired'));
        clearInterval(timer);
        toast.error(t('store.checkout.paymentExpired'));
      } else {
        const hours = Math.floor(timeRemaining / 3600);
        const minutes = Math.floor((timeRemaining % 3600) / 60);
        const seconds = timeRemaining % 60;
        setTimeLeft(`${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
        
        // Show warning when less than 5 minutes remaining
        if (timeRemaining <= 300 && timeRemaining % 60 === 0) {
          toast.error(
            t('store.checkout.expiringWarning', { minutes: Math.ceil(timeRemaining / 60) }),
            { duration: 4000 }
          );
        }
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [payment, t]);

  useEffect(() => {
    if (!reference) return;

    let pollCount = 0;
    const maxPolls = 360; // 30 minutes of polling

    const pollStatus = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.store.paymentDetails(reference));
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.error || `Failed to fetch payment status (${response.status})`);
        }
        const data = await response.json();
        setPayment(data);
        pollCount++;

        // Check if payment is already completed
        if (data.status === 'PAID') {
          if (lastStatus !== data.status) {
            toast.success(t('store.checkout.paymentSuccess'));
          }
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
          return;
        }

        if (lastStatus && lastStatus !== data.status) {
          if (data.status === 'EXPIRED') {
            toast.error(t('store.checkout.paymentExpired'));
          } else if (data.status === 'FAILED') {
            toast.error(t('store.checkout.paymentFailed'));
          }
        }
        setLastStatus(data.status);

        // Stop polling after max attempts
        if (pollCount >= maxPolls) {
          toast.error(t('store.checkout.pollingTimeout'));
          if (pollIntervalRef.current) {
            clearInterval(pollIntervalRef.current);
          }
          return;
        }
      } catch (err) {
        // Don't show error toast for every failed poll
        if (pollCount % 12 === 0) { // Show error every minute
          toast.error(t('store.checkout.pollingError'));
        }
      }
    };

    pollStatus(); // Initial poll
    pollIntervalRef.current = setInterval(pollStatus, 5000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [reference, lastStatus, t]);

  if (loading) {
    return (
      <PageTransition>
        <div className="min-h-screen bg-gray-900 py-24 flex justify-center items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        </div>
      </PageTransition>
    );
  }

  if (error || !payment) {
    return (
      <PageTransition>
        <div className="min-h-screen bg-gray-900 py-24 flex flex-col items-center">
          <div className="text-red-500 text-center mb-4">
            {error || t('store.checkout.paymentNotFound')}
          </div>
          <Button onClick={() => navigate('/store')}>
            {t('store.backToStore')}
          </Button>
        </div>
      </PageTransition>
    );
  }

  return (
    <PageTransition>
      <SEO
        title={t('store.checkout.pageTitle')}
        description={t('store.checkout.pageDescription')}
      />
      <div className="min-h-screen bg-gray-900">
        <div className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto">
            <div className="max-w-3xl mx-auto">
              <AnimatedSection animation="fade-up">
                {/* Back button */}
                <button 
                  onClick={() => navigate('/store')}
                  className="flex items-center text-gray-400 hover:text-white mb-8 group transition-colors duration-300"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    className="h-5 w-5 mr-2 transform transition-transform duration-300 group-hover:-translate-x-1" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  {t('store.backToStore')}
                </button>

                {/* Payment Status */}
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700/50 mb-6">
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-xl font-semibold text-white">
                        {t('store.checkout.paymentStatus')}
                      </h2>
                      <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                        payment.status === 'PAID' 
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : payment.status === 'EXPIRED'
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                          : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      }`}>
                        {t(`store.checkout.status.${payment.status}`)}
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-400">{t('store.checkout.reference')}</span>
                        <span className="text-white font-medium">{payment.reference}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">{t('store.checkout.amount')}</span>
                        <span className="text-white font-medium">
                          {t('store.checkout.currency', {
                            value: payment.amount,
                            formatParams: {
                              value: {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0
                              }
                            }
                          })}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">{t('store.checkout.paymentMethod')}</span>
                        <span className="text-white font-medium">{payment.payment_name}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">{t('store.checkout.timeLeft')}</span>
                        <span className={`font-medium ${timeLeft === t('store.checkout.expired') ? 'text-red-400' : 'text-white'}`}>
                          {timeLeft}
                        </span>
                      </div>
                    </div>
                    {payment.status === 'PAID' && (
                      <div className="mt-6 flex justify-center">
                        <Button onClick={() => navigate('/store')}>
                          {t('store.backToStore')}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Payment Details */}
                <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl overflow-hidden border border-gray-700/50 mb-6">
                  <div className="p-6">
                    <h2 className="text-xl font-semibold text-white mb-4">
                      {t('store.checkout.paymentDetails')}
                    </h2>
                    
                    {/* QRIS QR Code */}
                    {payment.payment_method === 'QRIS' && payment.qr_url && (
                      <div className="mb-6">
                        <div className="flex flex-col items-center space-y-4">
                          <div className="bg-white p-4 rounded-lg w-[320px] h-[320px] flex items-center justify-center">
                            <img 
                              src={payment.qr_url} 
                              alt={t('store.checkout.qrisQrCodeAlt')}
                              className="w-full h-full object-contain aspect-square"
                            />
                          </div>
                          <p className="text-gray-900 text-sm text-center font-medium bg-white px-4 py-2 rounded-lg">
                            {t('store.checkout.qrisInstruction')}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Payment Code for VA/Store */}
                    {payment.pay_code && (
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-400 mb-2">
                          {t('store.checkout.paymentCode')}
                        </label>
                        <div className="flex">
                          <input
                            type="text"
                            readOnly
                            value={payment.pay_code}
                            className="flex-1 bg-gray-900/50 border border-gray-600 rounded-l-lg px-4 py-3 text-white font-mono text-lg tracking-wider"
                          />
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(payment.pay_code);
                              toast.success(t('store.checkout.codeCopied'));
                            }}
                            className="px-6 bg-primary-500 text-white rounded-r-lg hover:bg-primary-600 transition-colors duration-200 font-medium"
                          >
                            {t('store.checkout.copy')}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Payment Instructions */}
                    <div className="space-y-6">
                      {payment.instructions.map((instruction, index) => (
                        <div key={index}>
                          <h3 className="text-lg font-medium text-white mb-3">
                            {instruction.title}
                          </h3>
                          <ol className="space-y-2">
                            {instruction.steps.map((step, stepIndex) => (
                              <li 
                                key={stepIndex}
                                className="text-gray-300 flex"
                                dangerouslySetInnerHTML={{ __html: `${stepIndex + 1}. ${step}` }}
                              />
                            ))}
                          </ol>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Help Text */}
                <div className="text-center text-gray-400">
                  <p>
                    {t('store.checkout.help')} {' '}
                    <a 
                      href="https://dsc.gg/kaizenmc" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary-400 hover:text-primary-300 underline font-medium"
                    >
                      {t('store.checkout.contactUs')}
                    </a>
                  </p>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    </PageTransition>
  );
} 